import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { 
  MessageCircle, Send, Loader2, User, Clock, Check, CheckCheck,
  AlertCircle, Shield, RefreshCw
} from 'lucide-react';

/**
 * YouthSecureMessaging - Secure messaging between youth and therapist
 * Available for Young Adults (16-17)
 */

const YouthSecureMessaging = ({ clientId, token, backendUrl, toast }) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [therapist, setTherapist] = useState(null);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${backendUrl}/api/youth-portal/messages?client_id=${clientId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        setTherapist(data.therapist || null);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Unable to load messages');
    } finally {
      setIsLoading(false);
    }
  }, [backendUrl, clientId, token]);

  useEffect(() => {
    fetchMessages();
    // Poll for new messages every 30 seconds
    const interval = setInterval(fetchMessages, 30000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isSending) return;

    const messageContent = inputValue.trim();
    setInputValue('');
    setIsSending(true);

    // Optimistic update
    const tempMessage = {
      id: `temp_${Date.now()}`,
      content: messageContent,
      sender_type: 'youth',
      created_at: new Date().toISOString(),
      status: 'sending'
    };
    setMessages(prev => [...prev, tempMessage]);

    try {
      const response = await fetch(`${backendUrl}/api/youth-portal/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          client_id: clientId,
          content: messageContent
        })
      });

      const data = await response.json();

      if (data.success) {
        // Replace temp message with real one
        setMessages(prev => prev.map(m => 
          m.id === tempMessage.id ? { ...data.message, status: 'sent' } : m
        ));
        toast?.success?.('Message sent');
      } else {
        // Mark as failed
        setMessages(prev => prev.map(m => 
          m.id === tempMessage.id ? { ...m, status: 'failed' } : m
        ));
        toast?.error?.(data.detail || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setMessages(prev => prev.map(m => 
        m.id === tempMessage.id ? { ...m, status: 'failed' } : m
      ));
      toast?.error?.('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sending':
        return <Clock className="w-3 h-3 text-gray-400" />;
      case 'sent':
        return <Check className="w-3 h-3 text-gray-400" />;
      case 'read':
        return <CheckCheck className="w-3 h-3 text-blue-500" />;
      case 'failed':
        return <AlertCircle className="w-3 h-3 text-red-500" />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600 mx-auto" />
          <p className="text-gray-600 mt-2">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[500px] bg-white rounded-lg border shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              {therapist?.name || 'Your Therapist'}
            </h3>
            <p className="text-xs text-gray-500">Secure messaging</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={fetchMessages}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No messages yet</p>
            <p className="text-sm text-gray-400">Send a message to start the conversation</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.sender_type === 'youth' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                  message.sender_type === 'youth'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                    : 'bg-white border border-gray-200 text-gray-800'
                }`}
              >
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                <div className={`flex items-center gap-1 mt-1 ${
                  message.sender_type === 'youth' ? 'justify-end' : 'justify-start'
                }`}>
                  <span className={`text-xs ${
                    message.sender_type === 'youth' ? 'text-white/70' : 'text-gray-400'
                  }`}>
                    {formatTime(message.created_at)}
                  </span>
                  {message.sender_type === 'youth' && getStatusIcon(message.status || 'sent')}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t bg-white">
        <div className="flex gap-2">
          <Textarea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message to your therapist..."
            className="resize-none"
            rows={2}
            disabled={isSending}
          />
          <Button
            onClick={sendMessage}
            disabled={!inputValue.trim() || isSending}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 px-6"
          >
            {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-xs text-gray-400 mt-2 text-center flex items-center justify-center gap-1">
          <Shield className="w-3 h-3" />
          Messages are encrypted and secure
        </p>
      </div>
    </div>
  );
};

export default YouthSecureMessaging;
